import { createContext, useContext, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { CitizenSession } from '@pmc/shared';
import { api } from './api';

/**
 * The visitor's session — which property they have located, and the support
 * details shown in the header.
 *
 * PID-as-identity: there is no login. `activePropertyId` is set server-side
 * whenever a property, bill, checkout or receipt is opened, and comes back in
 * the signed session cookie. The client mirrors it so the shell knows whether
 * to render the public header or the property-scoped one.
 */

export interface PortalConfig {
  samplePid: string;
  supportEmail: string;
  supportPhone: string;
  lang: string;
  session: CitizenSession;
}

interface SessionContextValue {
  config: PortalConfig | undefined;
  session: CitizenSession | undefined;
  isLoading: boolean;
  /** Re-read the session after an action that may have changed the active PID. */
  refresh: () => void;
  /** "Change Property" — drops the located PID server-side, then re-reads the session. */
  clearSession: () => Promise<void>;
}

const SessionContext = createContext<SessionContextValue | null>(null);

export const CONFIG_QUERY_KEY = ['portal-config'] as const;

export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: CONFIG_QUERY_KEY,
    queryFn: () => api.get<PortalConfig>('/config'),
    // The session changes as a side effect of other calls, so this must not be
    // served from a long cache.
    staleTime: 0,
  });

  const value = useMemo<SessionContextValue>(
    () => ({
      config: data,
      session: data?.session,
      isLoading,
      refresh: () => void queryClient.invalidateQueries({ queryKey: CONFIG_QUERY_KEY }),
      // "Change Property" — the closest this PID-as-identity model has to a
      // logout. The server-side cookie is already a full wipe (see
      // clearCitizenSession); clearing the whole client cache too means no
      // stale cached screen for the old property could ever flash back in
      // (e.g. via browser back) after this — defense-in-depth, since a new
      // property search wouldn't reuse the old property's cache keys anyway.
      //
      // queryClient.clear() alone isn't enough: it wipes the cache but does
      // not guarantee an immediate refetch of still-mounted queries (this
      // provider's own /config query never unmounts), so the in-memory
      // session mirror could stay stuck on stale data — cookie correctly
      // cleared server-side, but the app not yet aware of it — until
      // something unrelated happened to trigger a refetch. Force it instead
      // of hoping for one.
      clearSession: async () => {
        await api.post('/session/clear');
        queryClient.clear();
        await queryClient.refetchQueries({ queryKey: CONFIG_QUERY_KEY, type: 'active' });
      },
    }),
    [data, isLoading, queryClient],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const context = useContext(SessionContext);
  if (!context) throw new Error('useSession must be used inside <SessionProvider>');
  return context;
}

/** The located property, or null when the visitor has not entered a PID yet. */
export function useActiveProperty(): { propertyId: number | null; propertyNo: string | null } {
  const { session } = useSession();
  return {
    propertyId: session?.activePropertyId ?? null,
    propertyNo: session?.activePropertyNo ?? null,
  };
}
